import { useEffect, useState, type FormEvent } from 'react';
import {
  WebAuthnAbortService,
  browserSupportsWebAuthn,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
} from '@simplewebauthn/browser';
import { useI18n } from '@bravemove/i18n/react';
import type { AdminSecurityKey } from '@bravemove/types';
import { Banner, Button, Card, FormField, Modal, TextInput, useToast } from '@bravemove/ui-web';
import { adminSecurityKeysApi, type SecurityKeyCeremony } from '../api/securityKeys';
import { translateSecurityKeyError } from '../auth/securityKeyErrors';
import { ApiError } from '../auth/types';

/** Same rule as the API's SecurityKeyNameSchema, so the admin learns it here. */
const KEY_NAME_PATTERN = /^[\p{L}\p{M}\p{N} ._'’()#+&-]+$/u;
const KEY_NAME_MAX_LENGTH = 60;

type Translator = ReturnType<typeof useI18n>['t'];

/** The re-entered password gets its own wording; the login one mentions email. */
function translatePasswordStepError(err: unknown, t: Translator): string {
  if (err instanceof ApiError && err.code === 'INVALID_CREDENTIALS') {
    return t('admin:securityKeys.errors.wrongPassword');
  }
  return translateSecurityKeyError(err, t);
}

/**
 * F65 — the admin's own hardware security keys. Adding is two steps on purpose:
 * the password check first, then a separate button that opens the browser prompt.
 * Safari only allows that prompt right after a click, which a password check
 * (slow by design) could outlast.
 */
export function SecurityKeysCard() {
  const { t, locale } = useI18n();
  const [keys, setKeys] = useState<readonly AdminSecurityKey[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<AdminSecurityKey | null>(null);
  const [reloadCount, setReloadCount] = useState(0);
  const supported = browserSupportsWebAuthn();

  useEffect(() => {
    let cancelled = false;
    adminSecurityKeysApi.list().then(
      (next) => {
        if (cancelled) return;
        setKeys(next);
        setLoadError(null);
      },
      (err: unknown) => {
        if (!cancelled) setLoadError(translateSecurityKeyError(err, t));
      },
    );
    return () => {
      cancelled = true;
    };
  }, [t, reloadCount]);

  return (
    <Card>
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-h3 font-bold text-ink">{t('admin:securityKeys.title')}</h2>
            <p className="text-[13px] text-ink-2 leading-snug mt-1">
              {t('admin:securityKeys.body')}
            </p>
          </div>
          <Button variant="secondary" disabled={!supported} onClick={() => setAdding(true)}>
            {t('admin:securityKeys.add')}
          </Button>
        </div>

        {!supported ? <Banner tone="warning">{t('admin:securityKeys.unsupported')}</Banner> : null}
        {loadError ? <Banner tone="danger">{loadError}</Banner> : null}

        {keys && keys.length === 0 ? (
          <p className="text-[13px] text-ink-3">{t('admin:securityKeys.empty')}</p>
        ) : null}

        {keys && keys.length > 0 ? (
          <ul className="divide-y divide-hairline border-y border-hairline">
            {keys.map((key) => (
              <li key={key.id} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-ink truncate">{key.name}</div>
                  <div className="text-[12px] text-ink-3 mt-0.5">
                    {t('admin:securityKeys.addedOn', { date: formatDate(key.createdAt, locale) })}
                    {' · '}
                    {key.lastUsedAt
                      ? t('admin:securityKeys.lastUsedOn', {
                          date: formatDate(key.lastUsedAt, locale),
                        })
                      : t('admin:securityKeys.neverUsed')}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('admin:securityKeys.removeNamed', { name: key.name })}
                  onClick={() => setRemoveTarget(key)}
                >
                  {t('admin:securityKeys.remove')}
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {adding ? (
        <AddKeyModal
          onClose={() => setAdding(false)}
          onAdded={() => {
            setAdding(false);
            setReloadCount((count) => count + 1);
          }}
        />
      ) : null}

      {removeTarget ? (
        <RemoveKeyModal
          target={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onRemoved={() => {
            setRemoveTarget(null);
            setReloadCount((count) => count + 1);
          }}
        />
      ) : null}
    </Card>
  );
}

function AddKeyModal({
  onClose,
  onAdded,
}: {
  readonly onClose: () => void;
  readonly onAdded: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ceremony, setCeremony] =
    useState<SecurityKeyCeremony<PublicKeyCredentialCreationOptionsJSON> | null>(null);

  // Leaving the page must not leave the browser prompt open behind it.
  useEffect(() => () => WebAuthnAbortService.cancelCeremony(), []);

  const checkPassword = async (event?: FormEvent) => {
    event?.preventDefault();
    if (busy) return;
    const trimmed = name.trim();
    if (trimmed.length === 0 || !KEY_NAME_PATTERN.test(trimmed)) {
      setNameError(t('admin:securityKeys.errors.invalidName'));
      return;
    }
    setNameError(null);
    setError(null);
    setBusy(true);
    try {
      setCeremony(await adminSecurityKeysApi.beginAdd(password));
    } catch (err) {
      setError(translatePasswordStepError(err, t));
    } finally {
      setBusy(false);
    }
  };

  const tapKey = async () => {
    if (busy || !ceremony) return;
    setError(null);
    setBusy(true);
    try {
      const attestation = await startRegistration({ optionsJSON: ceremony.options });
      await adminSecurityKeysApi.finishAdd({
        challengeId: ceremony.challengeId,
        name: name.trim(),
        attestation,
      });
      toast.push({ tone: 'success', title: t('admin:securityKeys.addedToast') });
      onAdded();
    } catch (err) {
      // The browser never reached the server on a cancel, so the challenge is
      // still good. Any answer from the server spent it: go back a step.
      if (err instanceof ApiError) setCeremony(null);
      setError(translateSecurityKeyError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin:securityKeys.addTitle')}
      description={ceremony ? t('admin:securityKeys.useKeyBody') : t('admin:securityKeys.addBody')}
      showCloseButton={!busy}
      dismissOnBackdrop={!busy}
      dismissOnEscape={!busy}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t('admin:common.cancel')}
          </Button>
          {ceremony ? (
            <Button variant="primary" type="button" loading={busy} onClick={() => void tapKey()}>
              {t('admin:securityKeys.useKey')}
            </Button>
          ) : (
            <Button
              variant="primary"
              type="button"
              loading={busy}
              disabled={name.trim().length === 0 || password.length === 0}
              onClick={() => void checkPassword()}
            >
              {t('admin:securityKeys.continue')}
            </Button>
          )}
        </>
      }
    >
      {error ? (
        <Banner tone="danger" className="mb-4">
          {error}
        </Banner>
      ) : null}
      {ceremony ? null : (
        <form onSubmit={(event) => void checkPassword(event)} className="space-y-4">
          <FormField
            label={t('admin:securityKeys.nameLabel')}
            helper={t('admin:securityKeys.nameHelper')}
            error={nameError}
          >
            <TextInput
              value={name}
              maxLength={KEY_NAME_MAX_LENGTH}
              autoComplete="off"
              invalid={!!nameError}
              onChange={(event) => setName(event.target.value)}
            />
          </FormField>
          <FormField label={t('admin:securityKeys.passwordLabel')}>
            <TextInput
              type="password"
              value={password}
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
            />
          </FormField>
          {/* Lets Enter submit; the visible button lives in the modal footer. */}
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </form>
      )}
    </Modal>
  );
}

function RemoveKeyModal({
  target,
  onClose,
  onRemoved,
}: {
  readonly target: AdminSecurityKey;
  readonly onClose: () => void;
  readonly onRemoved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const remove = async (event?: FormEvent) => {
    event?.preventDefault();
    if (busy || password.length === 0) return;
    setError(null);
    setBusy(true);
    try {
      await adminSecurityKeysApi.remove(target.id, password);
      toast.push({ tone: 'success', title: t('admin:securityKeys.removedToast') });
      onRemoved();
    } catch (err) {
      setError(translatePasswordStepError(err, t));
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin:securityKeys.removeTitle', { name: target.name })}
      description={t('admin:securityKeys.removeBody')}
      showCloseButton={!busy}
      dismissOnBackdrop={!busy}
      dismissOnEscape={!busy}
      footer={
        <>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant="destructive"
            type="button"
            loading={busy}
            disabled={password.length === 0}
            onClick={() => void remove()}
          >
            {t('admin:securityKeys.removeConfirm')}
          </Button>
        </>
      }
    >
      {error ? (
        <Banner tone="danger" className="mb-4">
          {error}
        </Banner>
      ) : null}
      <form onSubmit={(event) => void remove(event)}>
        <FormField label={t('admin:securityKeys.passwordLabel')}>
          <TextInput
            type="password"
            value={password}
            autoComplete="current-password"
            onChange={(event) => setPassword(event.target.value)}
          />
        </FormField>
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Modal>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(new Date(iso));
  } catch {
    return iso;
  }
}
